import React, { useState } from 'react';
import { Badge, Button } from 'react-bootstrap';

const severityBadgeVariant = (severity) => {
  switch (severity) {
    case 'critical':
      return 'danger';
    case 'high':
      return 'warning';
    case 'medium':
      return 'info';
    default:
      return 'secondary';
  }
};

/**
 * Compact, collapsed-by-default card for an AI copilot analysis. Used both in the
 * ticket chat thread (in place of the old full-width internal-note bubble) and can
 * be reused anywhere else an AI draft needs a preview (e.g. the review queue).
 */
const AICopilotCard = ({ analysis, timestamp, onUseDraftReply, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (!analysis) {
    return null;
  }

  const questions = analysis.clarifying_questions || [];

  return (
    <div className="ai-copilot-card">
      <button
        type="button"
        className="ai-copilot-card-header"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <div className="ai-copilot-card-header-main">
          <i className="fas fa-wand-magic-sparkles ai-copilot-card-icon"></i>
          <span className="ai-copilot-card-title">AI Copilot</span>
          <Badge bg={severityBadgeVariant(analysis.severity)} className="ai-copilot-card-severity">
            {analysis.severity || 'unknown'}
          </Badge>
          {!expanded && (
            <span className="ai-copilot-card-summary-line">
              {analysis.summary || 'No AI summary available yet.'}
            </span>
          )}
        </div>
        <div className="ai-copilot-card-header-side">
          {timestamp && (
            <span className="ai-copilot-card-time">
              {new Date(timestamp).toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          )}
          <i className={`fas ${expanded ? 'fa-chevron-up' : 'fa-chevron-down'} ai-copilot-card-chevron`}></i>
        </div>
      </button>

      {expanded && (
        <div className="ai-copilot-card-body">
          <div className="ai-copilot-summary">
            {analysis.summary || 'No AI summary available yet.'}
          </div>

          <div className="ai-support-meta">
            <div className="ai-support-meta-item">
              <span className="ai-support-label">Issue</span>
              <strong>{(analysis.issue_type || 'unknown').replace(/_/g, ' ')}</strong>
            </div>
            <div className="ai-support-meta-item">
              <span className="ai-support-label">Module</span>
              <strong>{analysis.module || 'unknown'}</strong>
            </div>
            <div className="ai-support-meta-item">
              <span className="ai-support-label">Confidence</span>
              <strong>{analysis.confidence || 'unknown'}</strong>
            </div>
            <div className="ai-support-meta-item">
              <span className="ai-support-label">Escalate</span>
              <strong className={analysis.should_escalate ? 'text-danger' : 'text-success'}>
                {analysis.should_escalate ? 'Yes' : 'No'}
              </strong>
            </div>
          </div>

          {questions.length > 0 && (
            <div className="mt-3">
              <small className="text-muted d-block mb-2">Suggested Questions</small>
              <ul className="ai-support-list mb-0">
                {questions.map((question, index) => (
                  <li key={`${index}-${question}`}>{question}</li>
                ))}
              </ul>
            </div>
          )}

          {analysis.draft_reply && (
            <div className="mt-3">
              <small className="text-muted d-block mb-2">Draft Reply</small>
              <div className="ai-support-draft">{analysis.draft_reply}</div>
              {onUseDraftReply && (
                <div className="d-grid mt-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => onUseDraftReply(analysis.draft_reply)}
                  >
                    <i className="fas fa-wand-magic-sparkles me-2"></i>
                    Use Draft Reply
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AICopilotCard;
